import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  accessObjectsService,
  type AccessObjectFilters,
  type AccessObjectCreateInput,
  type AccessObjectUpdateInput,
} from '../api';
import type { AccessObject } from '@lognai/plugin-sdk';
import { queryKeys } from './query-keys';

export function useAccessObjects(
  filters?: AccessObjectFilters,
  options?: Omit<UseQueryOptions<AccessObject[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.accessObjects.list(filters || {}),
    queryFn: () => accessObjectsService.list(filters),
    ...options,
  });
}

export function useAccessObject(
  accessId: string | undefined,
  options?: Omit<UseQueryOptions<AccessObject>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.accessObjects.detail(accessId || ''),
    queryFn: () => {
      if (!accessId) throw new Error('Access ID is required');
      return accessObjectsService.getById(accessId);
    },
    enabled: !!accessId,
    ...options,
  });
}

export function useSystemAccessObjects(
  options?: Omit<UseQueryOptions<AccessObject[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.accessObjects.system,
    queryFn: () => accessObjectsService.getSystemAccessObjects(),
    ...options,
  });
}

export function useCreateAccessObject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AccessObjectCreateInput) => accessObjectsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accessObjects.lists() });
    },
  });
}

export function useUpdateAccessObject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accessId, data }: { accessId: string; data: AccessObjectUpdateInput }) =>
      accessObjectsService.update(accessId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accessObjects.detail(variables.accessId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.accessObjects.lists() });
    },
  });
}

export function useDeleteAccessObject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accessId: string) => accessObjectsService.delete(accessId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accessObjects.lists() });
    },
  });
}
